import {
  computed,
  inject,
  Injectable,
  signal,
  Signal,
  WritableSignal,
} from "@angular/core";
import { DashboardState } from "../../interfaces/dashboard-state.interface";
import {
  ResponseTestDto,
  ResponseVisitDto,
  CompletePhaseDto,
} from "../../api/models";
import { BookingStatus } from "../../enums";
import { LoggedUserService } from "@keus-template/auth";
import { DateFormatterService } from "../utils/date-formatter.service";
import { BookingsByDateMap } from "../../interfaces/booking-date.interface";
import { PathwayStateService } from "./pathway-state.service";

/**
 * Service responsible for managing dashboard state
 * Centralizes data access and state management for the dashboard view
 */
@Injectable({
  providedIn: "root",
})
export class DashboardStateService {
  /** Injected service for pathway state data */
  private readonly _pathwayStateService: PathwayStateService =
    inject(PathwayStateService);

  /** Injected service for logged user data */
  private readonly _loggedUserService: LoggedUserService =
    inject(LoggedUserService);

  /** Injected service for date formatting */
  private readonly _dateFormatterService: DateFormatterService =
    inject(DateFormatterService);

  /** Main state container for dashboard data */
  private readonly _state: WritableSignal<DashboardState> =
    signal<DashboardState>({
      error: null,
      currentDate: this._dateFormatterService.getCurrentFullDate(),
      patientFirstName: null,
      patientLastName: null,
    });

  /** Signal indicating if an error has occurred */
  public readonly hasError: Signal<boolean> = computed(
    () => !!this._state().error,
  );

  /** Signal providing current error message if any */
  public readonly error: Signal<string | null> = computed(
    () => this._state().error,
  );

  /** Signal providing current visits data from pathway */
  public readonly visits: Signal<ResponseVisitDto[]> = computed(() => {
    const pathwayData = this._pathwayStateService.steps();
    if (!pathwayData) return [];

    const allVisits: ResponseVisitDto[] = [];
    pathwayData.forEach((phase: CompletePhaseDto) => {
      if (phase.visits) {
        allVisits.push(...phase.visits);
      }
    });
    return allVisits;
  });

  /** Signal providing current tests data from pathway */
  public readonly tests: Signal<ResponseTestDto[]> = computed(() => {
    const pathwayData = this._pathwayStateService.steps();
    if (!pathwayData) return [];

    const allTests: ResponseTestDto[] = [];
    pathwayData.forEach((phase: CompletePhaseDto) => {
      if (phase.tests) {
        allTests.push(...phase.tests);
      }
    });
    return allTests;
  });

  /**
   * Signal providing the count of consultations (visits)
   * Computed dinamicamente dal signal delle visite
   */
  public readonly consultationCounter: Signal<number> = computed(() => {
    return this.visits().filter((visit) => {
      return visit.status !== BookingStatus.MISSED;
    }).length;
  });

  /**
   * Signal providing the count of prescriptions (tests)
   * Computed dinamicamente dal signal dei test
   */
  public readonly prescriptionCounter: Signal<number> = computed(() => {
    return this.tests().filter((test) => {
      return test.status !== BookingStatus.MISSED;
    }).length;
  });

  /**
   * Signal providing a map of next 4 reservations grouped by date
   * Returns an object where keys are date strings in 'YYYY-MM-DD' format
   * and values are arrays of visits/tests scheduled for that date
   */
  public readonly next4reservationsByDate: Signal<BookingsByDateMap> = computed(
    () => {
      const visitsData = this.visits() || [];
      const testsData = this.tests() || [];

      return this._manageNext4BookingsByDate(visitsData, testsData);
    },
  );

  /** Signal providing the current date in formato "DD Mese YYYY" */
  public readonly currentFormattedDate: Signal<string> = computed(
    () =>
      this._state().currentDate ||
      this._dateFormatterService.getCurrentFullDate(),
  );

  /** Signal providing patient first name */
  public readonly patientFirstName: Signal<string | null> = computed(
    () => this._state().patientFirstName,
  );

  /** Signal providing patient last name */
  public readonly patientLastName: Signal<string | null> = computed(
    () => this._state().patientLastName,
  );

  /**
   * Initializes the dashboard state
   * Sets patient details from logged user
   * Data is loaded from pathway state service (no separate API calls needed)
   */
  public initState(): void {
    const { firstName, lastName } = this._loggedUserService.currentUser();

    // Initialize state with patient details
    this._state.update((state) => ({
      ...state,
      error: null,
      patientFirstName: firstName,
      patientLastName: lastName,
    }));

    // No need to load visits/tests separately - they come from pathway
  }

  private _getLocalDateString(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  /**
   * Groups upcoming bookings by date and returns a map
   * @param visitsData Array of visits data
   * @param testsData Array of tests data
   * @returns Map with date string keys and arrays of bookings as values
   */
  private _manageNext4BookingsByDate(
    visitsData: ResponseVisitDto[],
    testsData: ResponseTestDto[],
  ): BookingsByDateMap {
    const currentDate: Date = new Date();

    // Create unified booking events with normalized date fields
    const allBookings = [
      ...visitsData
        .filter((visit) => visit.status === BookingStatus.SCHEDULED)
        .map((visit) => ({
          bookingDate: new Date(visit.bookingDate),
          originalData: visit,
          type: "visit" as const,
        })),
      ...testsData
        .filter((test) => test.status === BookingStatus.SCHEDULED)
        .map((test) => ({
          bookingDate: new Date(test.bookingDate ?? new Date()),
          originalData: test,
          type: "test" as const,
        })),
    ];

    // Filter out past bookings
    const futureBookings = allBookings.filter(
      (booking) => booking.bookingDate >= currentDate,
    );

    // Sort by date (ascending)
    const sortedBookings = futureBookings.sort(
      (a, b) => a.bookingDate.getTime() - b.bookingDate.getTime(),
    );

    // Take next 4 reservations
    const next4Bookings = sortedBookings.slice(0, 4);

    // Group bookings by date (YYYY-MM-DD format)
    const bookingsByDate: BookingsByDateMap = {};

    for (const booking of next4Bookings) {
      // Format date as YYYY-MM-DD
      const dateString = this._getLocalDateString(booking.bookingDate);

      // Initialize array for this date if it doesn't exist
      if (!bookingsByDate[dateString]) {
        bookingsByDate[dateString] = [];
      }

      // Add booking to the array for this date
      bookingsByDate[dateString].push(booking.originalData);
    }

    return bookingsByDate;
  }

  /**
   * Resets the error state
   * Clears any error messages while preserving other state data
   */
  public resetError(): void {
    this._state.update((state) => ({
      ...state,
      error: null,
    }));
  }

  /**
   * Resets the entire dashboard state
   * Returns to initial state with no data, loading, or errors
   */
  public resetState(): void {
    this._state.set({
      error: null,
      currentDate: null,
      patientFirstName: null,
      patientLastName: null,
    });
  }
}
